/**
 * WizardPreview Component
 * Step 2: Preview and edit pages
 */
import React, { useState, useEffect, useCallback } from 'react';
import { renderPageThumbnail } from './hooks/usePdfParser';

function PageThumbnail({ file, page, selected, onToggle, onSelect, isActive }) {
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (file.name.endsWith('.pdf') && file.file) {
      setLoading(true);
      renderPageThumbnail(file.file, page.pageNumber, 0.2)
        .then(dataUrl => {
          setThumbnail(dataUrl);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [file, page.pageNumber]);
  
  return (
    <div
      onClick={() => onSelect(file.id, page.pageNumber)}
      className={`
        relative rounded-lg border-2 p-1 cursor-pointer transition-all
        ${isActive
          ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800'
          : selected
            ? 'border-success-500'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
        }
      `}
    >
      {/* Checkbox */}
      <div 
        onClick={(e) => {
          e.stopPropagation();
          onToggle(file.id, page.pageNumber);
        }}
        className={`
          absolute top-2 left-2 w-5 h-5 rounded border-2 flex items-center justify-center
          theme-transition cursor-pointer z-10
          ${selected
            ? 'bg-success-500 border-success-500 text-white'
            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-primary-500'
          }
        `}
      >
        {selected && (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      
      {/* Edited indicator */}
      {page.edited && (
        <span className="absolute top-2 right-2 w-2 h-2 bg-yellow-500 rounded-full" title="Bearbeitet" />
      )}
      
      {/* Thumbnail */}
      <div className="aspect-[3/4] bg-gray-100 dark:bg-gray-800 rounded overflow-hidden flex items-center justify-center">
        {loading ? (
          <div className="animate-pulse w-full h-full bg-gray-200 dark:bg-gray-700" />
        ) : thumbnail ? (
          <img src={thumbnail} alt={`Seite ${page.pageNumber}`} className="w-full h-full object-cover" />
        ) : (
          <div className="text-gray-400 dark:text-gray-500 text-center p-2">
            <svg className="w-12 h-12 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span className="text-xs">{page.text.substring(0, 50)}...</span>
          </div>
        )}
      </div>
      
      {/* Page number */}
      <div className={`
        text-center text-xs mt-1 font-medium
        ${selected ? 'text-success-600 dark:text-success-400' : 'text-gray-500 dark:text-gray-400'}
      `}>
        S. {page.pageNumber}
      </div>
    </div>
  );
}

function WizardPreview({
  files,
  selectedPageCount,
  totalPageCount,
  onTogglePage,
  onSelectAll,
  onSelectRange,
  onUpdatePageText,
  onPrev,
  onNext
}) {
  const [selectedFile, setSelectedFile] = useState(files[0]?.id || null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [rangeInput, setRangeInput] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState('');
  
  // Get current file
  const currentFile = files.find(f => f.id === selectedFile);
  const currentPage = currentFile?.pages.find(p => 
    selectedPage?.fileId === currentFile.id && p.pageNumber === selectedPage?.pageNumber
  );
  
  // Handle page select for preview
  const handlePageSelect = useCallback((fileId, pageNumber) => {
    setSelectedPage({ fileId, pageNumber });
    const file = files.find(f => f.id === fileId);
    const page = file?.pages.find(p => p.pageNumber === pageNumber);
    if (page) {
      setEditText(page.text);
      setEditMode(false);
    }
  }, [files]);
  
  // Handle range selection
  const handleApplyRange = useCallback(() => {
    if (!rangeInput || !selectedFile) return;
    
    // Parse range like "1-10, 15-20, 25"
    const ranges = rangeInput.split(',').map(r => r.trim());
    const selectedPages = new Set();
    
    ranges.forEach(range => {
      if (range.includes('-')) {
        const [start, end] = range.split('-').map(n => parseInt(n.trim()));
        for (let i = start; i <= end; i++) {
          selectedPages.add(i);
        }
      } else {
        selectedPages.add(parseInt(range));
      }
    });
    
    // Apply selection
    const file = files.find(f => f.id === selectedFile);
    if (file) {
      const minPage = Math.min(...selectedPages);
      const maxPage = Math.max(...selectedPages);
      onSelectRange(selectedFile, minPage, maxPage);
    }
  }, [rangeInput, selectedFile, files, onSelectRange]);
  
  // Save edited text
  const handleSaveEdit = useCallback(() => {
    if (selectedPage && editText !== currentPage?.text) {
      onUpdatePageText(selectedPage.fileId, selectedPage.pageNumber, editText);
    }
    setEditMode(false);
  }, [selectedPage, editText, currentPage, onUpdatePageText]);
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Seiten-Vorschau
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Wähle die Seiten aus, die verarbeitet werden sollen
        </p>
      </div>
      
      {/* File Tabs (if multiple files) */}
      {files.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {files.map(file => (
            <button
              key={file.id}
              onClick={() => setSelectedFile(file.id)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium theme-transition
                ${selectedFile === file.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }
              `}
            >
              {file.name} ({file.pages.filter(p => p.selected).length}/{file.totalPages})
            </button>
          ))}
        </div>
      )}
      
      {/* Selection Controls */}
      <div className="flex flex-wrap items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <button
          onClick={() => onSelectAll(selectedFile, true)}
          className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 theme-transition flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Alle
        </button>
        <button
          onClick={() => onSelectAll(selectedFile, false)}
          className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 theme-transition flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Keine
        </button>
        
        <div className="flex-1" />
        
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={rangeInput}
            onChange={(e) => setRangeInput(e.target.value)}
            placeholder="z.B. 1-10, 15-20"
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 w-40"
          />
          <button
            onClick={handleApplyRange}
            className="px-3 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 theme-transition"
          >
            Anwenden
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Page Grid */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="font-medium text-gray-800 dark:text-gray-200 mb-3">
            Seiten ({currentFile?.pages.filter(p => p.selected).length || 0} von {currentFile?.totalPages || 0} ausgewählt)
          </h3>
          
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 max-h-96 overflow-y-auto">
            {currentFile?.pages.map(page => (
              <PageThumbnail
                key={`${currentFile.id}-${page.pageNumber}`}
                file={currentFile}
                page={page}
                selected={page.selected}
                onToggle={onTogglePage}
                onSelect={handlePageSelect}
                isActive={selectedPage?.fileId === currentFile.id && selectedPage?.pageNumber === page.pageNumber}
              />
            ))}
          </div>
        </div>
        
        {/* Preview Panel */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-800 dark:text-gray-200">
              {selectedPage 
                ? `Seite ${selectedPage.pageNumber} - Vorschau`
                : 'Seite zum Anzeigen auswählen'
              }
            </h3>
            {selectedPage && !editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Bearbeiten
              </button>
            )}
          </div>
          
          {selectedPage && currentPage ? (
            editMode ? (
              <div className="space-y-3">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full h-72 p-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 bg-success-500 text-white rounded hover:bg-success-600 theme-transition"
                  >
                    Speichern
                  </button>
                  <button
                    onClick={() => {
                      setEditText(currentPage.text);
                      setEditMode(false);
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 theme-transition"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              <div className="h-72 overflow-y-auto p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {currentPage.text || <span className="text-gray-400 italic">Kein Text auf dieser Seite</span>}
              </div>
            )
          ) : (
            <div className="h-72 flex items-center justify-center text-gray-400 dark:text-gray-500">
              Klicke auf eine Seite um den Inhalt zu sehen
            </div>
          )}
        </div>
      </div>
      
      {/* Summary */}
      <div className="text-center text-gray-600 dark:text-gray-400">
        <strong>{selectedPageCount}</strong> von <strong>{totalPageCount}</strong> Seiten ausgewählt
      </div>
      
      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onPrev}
          className="px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 theme-transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Zurück
        </button>
        
        <button
          onClick={onNext}
          disabled={selectedPageCount === 0}
          className={`
            px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 theme-transition
            ${selectedPageCount > 0
              ? 'bg-primary-500 hover:bg-primary-600 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Weiter
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default WizardPreview;
